<template>
  <div class="change-panel">
    <div class="change-head">
      <h3 class="change-title">What changed, and why</h3>
      <span class="change-count">{{ changeCountLabel }}</span>
    </div>

    <div class="change-body">
      <p v-if="!changes.length" class="change-empty">
        Nothing was altered on this page.
      </p>

      <article
        v-for="change in decoratedChanges"
        :id="`solution-change-${change.id}`"
        :key="change.key"
        class="change-card"
        :class="{ 'is-active': change.id === activeChangeId }"
        @click="$emit('select-change', change.id)"
      >
        <div class="change-card-head">
          <span class="change-id">{{ change.id }}</span>
          <span class="change-kind" :class="`tone-${change.kindTone}`">{{ change.kindLabel }}</span>
          <span class="change-lines">{{ change.lineHint }}</span>
        </div>

        <h4 class="change-summary">{{ change.summary }}</h4>
        <p class="change-where">
          on <strong>{{ change.orientation.noun }}</strong>
          <template v-if="change.orientation.name"> — <em>“{{ change.orientation.name }}”</em></template>
        </p>

        <div v-for="(value, index) in change.values" :key="`${change.key}-${index}`" class="value-row">
          <div class="value-label">{{ value.label }}</div>
          <div v-if="value.was" class="value-line is-was">
            <span class="value-tag">was</span>
            <span class="value-text" :title="value.was">{{ value.was }}</span>
          </div>
          <div v-if="value.now" class="value-line is-now">
            <span class="value-tag">now</span>
            <span class="value-text" :title="value.now">{{ value.now }}</span>
          </div>
          <p v-else-if="value.was" class="value-line is-gone">We took this out and did not replace it.</p>
          <blockquote v-if="value.quote" class="value-quote" :class="`quote-${value.tone}`">
            <span class="quote-text">“{{ value.quote }}”</span>
            <span class="quote-meta">
              <span class="quote-pill" :class="`pill-${value.tone}`">{{ value.levelLabel }}</span>
              <span class="quote-found">{{ value.foundIn }}</span>
            </span>
          </blockquote>
          <p v-else-if="value.provenance" class="value-provenance" :class="`prov-${value.tone}`">{{ value.provenance }}</p>
        </div>

        <p v-if="!change.values.length" class="change-novalues">{{ change.noValuesLine }}</p>

        <p v-if="change.why" class="change-why">{{ change.why }}</p>
        <p v-if="change.fixes" class="change-fixes"><span>Fixes:</span> {{ change.fixes }}</p>
      </article>
    </div>
  </div>
</template>

<script>
import {
  changeKindMetaFor,
  changeLineHintFor,
  changeNoValuesLineFor,
  changeOrientationFor,
  changeSummaryFor,
  issueProblemFor,
  propertyNounFor,
  SOLUTION_CONTRACT_VERSION,
  sourceFoundInFor,
  sourceLevelMetaFor,
  valueProvenanceFor
} from '@/utils/solutionSchema'
import { asList } from '@/utils/diagnosisFindings'

export default {
  name: 'SchemaSolutionChangeList',
  props: {
    changes: {
      type: Array,
      default: () => []
    },
    lines: {
      type: Array,
      default: () => []
    },
    activeChangeId: {
      type: String,
      default: null
    },
    contractVersion: {
      type: Number,
      default: SOLUTION_CONTRACT_VERSION
    }
  },
  emits: ['select-change'],
  computed: {
    changeCountLabel() {
      const count = this.changes.length
      return `${count} ${count === 1 ? 'change' : 'changes'}`
    },
    decoratedChanges() {
      return asList(this.changes).map((change, index) => {
        const kind = changeKindMetaFor(change && change.kind)
        const id = String((change && change.id) || '')
        const values = asList(change && change.values).map((value) => {
          const source = value && value.source
          const level = sourceLevelMetaFor(source && source.level)
          return {
            label: propertyNounFor(value && value.field) || 'What it says',
            was: this.plainValue(value && value.was),
            now: this.plainValue(value && value.now),
            quote: String((source && source.quote) || ''),
            foundIn: source ? sourceFoundInFor(source.found_in) : '',
            provenance: valueProvenanceFor(source),
            levelLabel: level.label,
            tone: level.tone
          }
        })

        return {
          id,
          key: id || `i${index}`,
          kindLabel: kind.label,
          kindTone: kind.tone,
          lineHint: changeLineHintFor(change && change.id, this.lines),
          summary: changeSummaryFor(change, this.contractVersion),
          orientation: changeOrientationFor(change),
          why: String((change && change.why) || ''),
          noValuesLine: changeNoValuesLineFor(change),
          fixes: change && change.repairs ? issueProblemFor(change.repairs) : '',
          values
        }
      })
    }
  },
  methods: {
    plainValue(value) {
      if (value == null || value === '') return ''
      if (typeof value === 'string') return value
      if (typeof value === 'number' || typeof value === 'boolean') return String(value)
      return 'structured detail'
    }
  }
}
</script>

<style scoped>
.change-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.change-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid var(--brand-sand-300);
  border-bottom: 0;
  border-radius: 14px 14px 0 0;
  background: #fff;
}

.change-title {
  font-family: 'Lora', serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.change-count {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  color: #8496bc;
}

.change-body {
  border: 1px solid var(--brand-sand-300);
  border-radius: 0 0 14px 14px;
  background: var(--brand-sand-100);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 620px;
  overflow: auto;
}

.change-empty {
  margin: 0;
  font-size: 13px;
  color: var(--brand-ink-800);
}

.change-card {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.change-card.is-active {
  border-color: var(--brand-ocean-600);
  box-shadow: 0 0 0 3px rgba(33, 105, 168, 0.18);
}

.change-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.change-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-ocean-600);
  background: var(--brand-ocean-100);
  border-radius: 5px;
  padding: 2px 6px;
}

.change-kind {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.tone-positive {
  color: #127a6a;
}

.tone-negative {
  color: #9b2c2c;
}

.tone-warn {
  color: #92610a;
}

.tone-neutral {
  color: #8496bc;
}

.change-lines {
  margin-left: auto;
  font-size: 11px;
  color: #8496bc;
  white-space: nowrap;
}

.change-summary {
  font-family: 'Lora', serif;
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 3px;
  line-height: 1.3;
}

.change-where {
  font-size: 12px;
  color: #8496bc;
  margin: 0 0 10px;
}

.change-where strong {
  color: var(--brand-ink-800);
}

.value-row {
  border-top: 1px solid var(--brand-sand-300);
  padding-top: 9px;
  margin-bottom: 9px;
}

.value-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--brand-ink-800);
  margin-bottom: 5px;
}

.value-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 4px;
}

.value-tag {
  flex: none;
  width: 26px;
  font-size: 11px;
  font-weight: 700;
  color: #8496bc;
}

.value-text {
  overflow-wrap: anywhere;
}

.value-line.is-was .value-text {
  font-size: 13px;
  font-weight: 400;
  color: #8496bc;
}

.value-line.is-now .value-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-ink-950);
}

.value-line.is-gone {
  font-size: 12.5px;
  color: var(--brand-ink-800);
}

.value-quote {
  margin: 0;
  border-left: 3px solid var(--brand-ocean-400);
  background: var(--brand-ocean-100);
  border-radius: 0 8px 8px 0;
  padding: 9px 11px;
}

.quote-inferred {
  border-left-color: var(--brand-amber-400);
  background: var(--brand-amber-100);
}

.quote-text {
  display: block;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 6px;
}

.quote-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.quote-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #fff;
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 700;
}

.pill-quoted {
  color: var(--brand-ocean-600);
}

.pill-inferred {
  color: #92610a;
}

.quote-found {
  font-size: 11px;
  color: #8496bc;
  white-space: nowrap;
}

.value-provenance {
  margin: 0;
  border-left: 3px solid var(--brand-ocean-400);
  background: var(--brand-ocean-100);
  border-radius: 0 8px 8px 0;
  padding: 6px 11px;
  font-size: 12px;
  font-weight: 600;
  color: var(--brand-ocean-600);
}

.value-provenance.prov-inferred {
  border-left-color: var(--brand-amber-400);
  background: var(--brand-amber-100);
  color: #92610a;
}

.change-novalues,
.change-why {
  margin: 0 0 9px;
  padding-top: 9px;
  border-top: 1px solid var(--brand-sand-300);
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--brand-ink-800);
}

.change-fixes {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--brand-ink-800);
}

.change-fixes span {
  font-weight: 700;
  color: var(--brand-ink-950);
}
</style>
